import React, { useMemo, useEffect, useState } from "react";
import PropTypes from "prop-types";
import "./BettingDistribution.css";
import { Pie, PieChart, ResponsiveContainer, Cell } from "recharts";

const TOKEN_COLORS = {
  BTC: "#F7931A",
  ETH: "#627EEA",
  XRP: "#23292F",
  SOL: "#14F195",
  DOT: "#E6007A",
  ADA: "#0033AD",
  AVAX: "#E84142",
  LINK: "#2A5ADA",
  MATIC: "#8247E5",
  UNI: "#FF007A",
};

const getTokenColor = (tokenSymbol) => {
  if (!tokenSymbol) return "#6C5DD3";
  const upperSymbol = tokenSymbol.toUpperCase();
  return (
    TOKEN_COLORS[upperSymbol] ||
    `hsl(${Math.abs(
      upperSymbol
        .split("")
        .reduce((acc, char) => char.charCodeAt(0) + ((acc << 5) - acc), 0) % 360
    )}, 70%, 45%)`
  );
};

const BettingDistribution = ({ participants, tokens }) => {
  const [pieData, setPieData] = useState([]);
  const [total, setTotal] = useState(0);

  const distribution = useMemo(() => {
    const betsByToken = tokens.map((token) => ({
      name: token.symbol,
      value: 0,
      totalBets: 0,
    }));

    participants.forEach((participant) => {
      const tokenData = betsByToken.find((t) => t.name === participant.token);
      if (tokenData) {
        tokenData.value += participant.bet;
        tokenData.totalBets += 1;
      }
    });

    return betsByToken.filter((token) => token.value > 0);
  }, [participants, tokens]);

  useEffect(() => {
    const data = distribution.map((token) => ({
      name: token.name,
      value: token.value,
      color: getTokenColor(token.name),
    }));
    setTotal(data.reduce((acc, curr) => acc + curr.value, 0));
    setPieData(data);
  }, [distribution]);
  return (
    <div className="betting-distribution flex items-center">
      <div className="distribution-chart h-52 w-52">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={pieData}
              innerRadius="60%"
              outerRadius="100%"
              paddingAngle={4}
              dataKey="value"
              cornerRadius={4}
              stroke="transparent"
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="">
        {pieData.map((entry) => (
          <div key={entry.name} className="legend-item">
            <div className="legend-symbol">
              <div
                className="color-indicator"
                style={{ backgroundColor: entry.color }}
              />
              <span>{entry.name}</span>
            </div>
            <span className="bet-percentage">
              {(entry.value / total * 100).toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

BettingDistribution.propTypes = {
  participants: PropTypes.arrayOf(
    PropTypes.shape({
      token: PropTypes.string.isRequired,
      bet: PropTypes.number.isRequired,
    })
  ).isRequired,
  tokens: PropTypes.arrayOf(
    PropTypes.shape({
      symbol: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default BettingDistribution;
